import { Select } from '@mantine/core'
import type {
  TValuesCurrentBlocks,
  TValuesCurrentHeatmapPoints,
  TValuesCurrentPoints,
  TValuesCurrentProperties,
} from 'App/Map/types'
import { translate } from 'i18n/i18n'
import { useMemo } from 'react'
import { SharedSettings } from 'settings/SharedSettings'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { mapControlsStore } from 'stores/mapControlsStore/mapControlsStore'
import { MAP_VISUAL } from 'stores/mapControlsStore/types'
import { unitConverter } from 'utils/unitConverter/unitConverter'

const updateMapVisuals = (newValueType: TValuesCurrentPoints & TValuesCurrentHeatmapPoints) => {
  mapControlsStore.setState((s) => ({
    ...s,
    mapVisualValueGroup: {
      ...s.mapVisualValueGroup,
      [MAP_VISUAL.POINT]: {
        ...s.mapVisualValueGroup[MAP_VISUAL.POINT],
        chill: {
          ...(s.mapVisualValueGroup[MAP_VISUAL.POINT].chill ?? { mItem: 'M_InBlockPoint' }),
          valueType: newValueType,
        },
      },
      [MAP_VISUAL.PROPERTY]: {
        ...s.mapVisualValueGroup[MAP_VISUAL.PROPERTY],
        chill: {
          ...(s.mapVisualValueGroup[MAP_VISUAL.PROPERTY].chill ?? { mItem: 'M_Property' }),
          valueType: newValueType.replace('Sum_', 'SumMinMedianMax_') as TValuesCurrentProperties,
        },
      },
      [MAP_VISUAL.SCD]: {
        ...s.mapVisualValueGroup[MAP_VISUAL.SCD],
        chill: {
          ...(s.mapVisualValueGroup[MAP_VISUAL.SCD].chill ?? { mItem: 'M_SCD' }),
          valueType: newValueType,
        },
      },
      [MAP_VISUAL.BLOCK]: {
        ...s.mapVisualValueGroup[MAP_VISUAL.BLOCK],
        chill: {
          ...(s.mapVisualValueGroup[MAP_VISUAL.BLOCK].chill ?? { mItem: 'M_SCD' }),
          valueType: newValueType.replace('Sum_', 'SumMinMedianMax_') as TValuesCurrentBlocks,
        },
      },
    },
  }))
}

export const ChillSecondaryDropdown = () => {
  const value = mapControlsStore.useSelector(
    (s) => s.mapVisualValueGroup?.[MAP_VISUAL.POINT]?.chill?.valueType,
  )

  const properties = fieldAssetStore.useSelector((s) => s.properties)
  const hasSouthernHemisphereProperties = Object.values(properties ?? {}).some((p) => p.isSouthernHemisphere)
  const hasNorthernHemisphereProperties = Object.values(properties ?? {}).some((p) => !p.isSouthernHemisphere)

  const data: { group: string; items: { value: TValuesCurrentPoints; label: string }[] }[] = useMemo(() => {
    const returner: { group: string; items: { value: TValuesCurrentPoints; label: string }[] }[] = []

    if (hasSouthernHemisphereProperties) {
      returner.push({
        group: translate.dates.monthLabel('MAR'),
        items: [
          {
            value: 'chillHoursLessThan45_sinceMar1Sum_hours',
            label: unitConverter.chillCumulativeHoursBelow45FMAR().titleWithoutUnit(),
          },
          {
            value: 'chillHoursBetween32And45_sinceMar1Sum_hours',
            label: unitConverter.chillCumulativeHoursBetween32and45FMAR().titleWithoutUnit(),
          },
          {
            value: 'chillPortions_sinceMar1Sum_portions',
            label: unitConverter.chillCumulativePortionsMAR().titleWithoutUnit(),
          },
        ],
      })

      returner.push({
        group: translate.dates.monthLabel('MAY'),
        items: [
          {
            value: 'chillHoursLessThan45_sinceMay1Sum_hours',
            label: unitConverter.chillCumulativeHoursBelow45FMAY().titleWithoutUnit(),
          },
          {
            value: 'chillHoursBetween32And45_sinceMay1Sum_hours',
            label: unitConverter.chillCumulativeHoursBetween32and45FMAY().titleWithoutUnit(),
          },
          {
            value: 'chillPortions_sinceMay1Sum_portions',
            label: unitConverter.chillCumulativePortionsMAY().titleWithoutUnit(),
          },
        ],
      })
    }

    if (hasNorthernHemisphereProperties) {
      returner.push({
        group: translate.dates.monthLabel('SEP'),
        items: [
          {
            value: 'chillHoursLessThan45_sinceSep1Sum_hours',
            label: unitConverter.chillCumulativeHoursBelow45FSEP().titleWithoutUnit(),
          },
          {
            value: 'chillHoursBetween32And45_sinceSep1Sum_hours',
            label: unitConverter.chillCumulativeHoursBetween32and45FSEP().titleWithoutUnit(),
          },
          {
            value: 'chillPortions_sinceSep1Sum_portions',
            label: unitConverter.chillCumulativePortionsSEP().titleWithoutUnit(),
          },
        ],
      })

      returner.push({
        group: translate.dates.monthLabel('NOV'),
        items: [
          {
            value: 'chillHoursLessThan45_sinceNov1Sum_hours',
            label: unitConverter.chillCumulativeHoursBelow45FNOV().titleWithoutUnit(),
          },
          {
            value: 'chillHoursBetween32And45_sinceNov1Sum_hours',
            label: unitConverter.chillCumulativeHoursBetween32and45FNOV().titleWithoutUnit(),
          },
          {
            value: 'chillPortions_sinceNov1Sum_portions',
            label: unitConverter.chillCumulativePortionsNOV().titleWithoutUnit(),
          },
        ],
      })
    }

    return returner
  }, [hasNorthernHemisphereProperties, hasSouthernHemisphereProperties, value])

  return (
    <Select
      data={data}
      value={value}
      onChange={(newValueType) => {
        if (newValueType) updateMapVisuals(newValueType as TValuesCurrentPoints & TValuesCurrentHeatmapPoints)
      }}
      styles={SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER}
      comboboxProps={{ withinPortal: true }}
    />
  )
}
